import { useState } from "react";
import { Loader2, FileText } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { TELEPHONY_CALL_TRANSCRIBE_URL } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

// Shared by CallHistory (per record) and the Call Log page.

export const CALL_STATUS_TONE = {
    completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    answered:  "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    missed:    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    failed:    "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    ringing:   "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    initiated: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
};

const TRANSCRIPT_BADGE = {
    queued:     ["Queued",        "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"],
    processing: ["Transcribing…", "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"],
    failed:     ["Transcript failed", "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"],
    done:       ["Transcript",    "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"],
};

// The recording is proxied through our backend with the JWT, so it goes
// through RecordingPlayer's blob fetch rather than a bare <audio src>.
export const RECORDING_ENDPOINT = "telephony/calls/{id}/recording/";

export const fmtDuration = (secs) => {
    if (secs == null) return "—";
    const s = Math.round(secs);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// Only an answered call with a recording has anything to transcribe; a failed
// transcript may be retried.
export const canTranscribe = (c) =>
    c.status === "completed" && c.has_recording && ["", "failed", null, undefined].includes(c.transcript_status);

export const CallStatusBadge = ({ status }) => (
    <span className={cn("px-1.5 py-0.5 rounded-full text-[10px] font-medium capitalize",
        CALL_STATUS_TONE[status] || CALL_STATUS_TONE.initiated)}>
        {status}
    </span>
);

export const TranscriptBadge = ({ status }) => {
    const meta = TRANSCRIPT_BADGE[status];
    if (!meta) return null;
    return (
        <span className={cn("px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap", meta[1])}>
            {meta[0]}
        </span>
    );
};

/** Queues one call for transcription; `onQueued` lets the list refresh. */
export const TranscribeButton = ({ callId, onQueued, className }) => {
    const { create } = useApiMethods();
    const [pending, setPending] = useState(false);

    const run = () => {
        setPending(true);
        create(TELEPHONY_CALL_TRANSCRIBE_URL(callId), {})
            .then((res) => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message || "Queued for transcription");
                    onQueued?.();
                } else ToastMsg("error", res?.message || "Could not queue the transcript");
            })
            .catch((err) => ToastMsg("error", err?.response?.data?.message || "Could not queue the transcript"))
            .finally(() => setPending(false));
    };

    return (
        <button type="button" onClick={run} disabled={pending} title="Transcribe this call"
            className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50 whitespace-nowrap", className)}>
            {pending ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
            Transcribe
        </button>
    );
};
